import { useState } from 'react';
import { motion } from 'framer-motion';
import { Controller, useFormContext } from 'react-hook-form';
import { Icon, Accordion, AccordionDetails, AccordionSummary, Typography, Card } from '@material-ui/core';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import _ from '@lodash';
import ConfirmDialog from 'modules/ui/component/ConfirmDialog';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';

const titleMessageSeparator = '\u00A0\u00A0-\u00A0\u00A0';

const useStyles = makeStyles(theme => ({
	card: {
		background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
		color: theme.palette.text.secondary
	}
}));

function EditFormSubItem(props) {
	const classes = useStyles();
	const methods = useFormContext();
	const { watch, control } = methods;
	const id = watch(`${props.fieldPrefix}id`);
	const [expanded, setExpanded] = useState(id == null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const name = _.isFunction(props.fieldConfig.watchTitleKey)
		? props.fieldConfig.watchTitleKey(props.fieldPrefix, watch())
		: watch(`${props.fieldPrefix}${_.isUndefined(props.fieldConfig.watchTitleKey) ? 'name' : props.fieldConfig.watchTitleKey}`);
	const configWatchers = {};

	props.fieldConfig.fields.forEach(fieldRow => {
		if (fieldRow.watchThis) {
			configWatchers[fieldRow.key] = watch(`${props.fieldPrefix}${fieldRow.key}`);
		}
	});

	const handleDirectionOnClick = direction => {
		if (props.addReference) props.onAdd(direction);
		else if (props.selectedToMoveTarget) props.onMove(direction);
	};

	return (
		<motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1, transition: { delay: 0.05 * props.order } }}>
			<Accordion className="border-0 shadow-0 overflow-hidden" expanded={expanded}>
				<AccordionSummary expandIcon={<ExpandMoreIcon onClick={() => setExpanded(!expanded)} />} classes={{ root: 'flex overflow-hidden border border-solid rounded-16 mb-16', content: 'items-center' }}>
					<div className="flex flex-grow flex-col md:flex-row">
						<Typography className="font-medium flex text-xs md:text-normal">{name || 'Új elem'}</Typography>
						<motion.div className="flex" initial="hidden" animate={props.selectedToMoveBase || props.selectedToMoveTarget ? 'visible' : 'hidden'} variants={{ visible: { opacity: 1, x: 0 }, hidden: { opacity: 0, x: 40 } }}>
							<Typography className="opacity-50 hidden md:block text-xs md:text-normal">{props.selectedToMoveBase || props.selectedToMoveTarget ? titleMessageSeparator : ''}</Typography>
							<Typography className="opacity-50 text-xs md:text-normal">
								{props.selectedToMoveBase || props.selectedToMoveTarget ? (props.selectedToMoveBase ? 'Áthelyezendő' : '') + (props.selectedToMoveTarget ? 'Áthelyezés célja' : '') : ''}
							</Typography>
						</motion.div>
					</div>
					<motion.div
						className="flex items-center"
						initial="hidden"
						animate={props.selectedToMoveTarget || props.addReference ? 'visible' : 'hidden'}
						variants={{ visible: { opacity: 1, display: 'flex' }, hidden: { opacity: 0, transitionEnd: { display: 'none' } } }}
					>
						<motion.div initial="hidden" animate={props.selectedToMoveTarget || props.addReference ? 'visible' : 'hidden'} variants={{ visible: { x: 0, opacity: 1 }, hidden: { x: -60, opacity: 0 } }}>
							<Button className="whitespace-nowrap mr-8" variant="contained" color="secondary" startIcon={<Icon className="hidden sm:flex">keyboard_arrow_up</Icon>} onClick={() => handleDirectionOnClick(-1)}>
								<span>Előtte</span>
							</Button>
						</motion.div>

						<motion.div initial="hidden" animate={props.selectedToMoveTarget || props.addReference ? 'visible' : 'hidden'} variants={{ visible: { x: 0, opacity: 1 }, hidden: { x: -40, opacity: 0 } }}>
							<Button className="whitespace-nowrap mr-8" variant="contained" color="secondary" startIcon={<Icon className="hidden sm:flex">keyboard_arrow_down</Icon>} onClick={() => handleDirectionOnClick(1)}>
								<span>Utána</span>
							</Button>
						</motion.div>
					</motion.div>
					{!props.readOnly && props.fieldConfig.orderable ? (
						<IconButton aria-label="add" onClick={() => props.onSelectToAdd()}>
							{props.addReference ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
						</IconButton>
					) : null}

					{!props.readOnly && props.fieldConfig.orderable ? (
						<IconButton aria-label="move" onClick={() => props.onSelectToMove()}>
							{props.selectedToMoveBase || props.selectedToMoveTarget ? <CloseIcon fontSize="small" /> : <DragIndicatorIcon fontSize="small" />}
						</IconButton>
					) : null}
					{!props.readOnly ? (
						<IconButton aria-label="delete" onClick={() => setShowDeleteConfirm(true)}>
							<DeleteIcon fontSize="small" />
						</IconButton>
					) : null}
				</AccordionSummary>

				<AccordionDetails className="flex flex-col px-0 pt-0 -mt-16">
					<Card className={clsx(classes.card, 'w-full rounded-16 shadow p-24 mt-16')}>
						<div>
							<div className="flex flex-row items-center">
								<Icon className="text-20 mr-8 opacity-30">settings</Icon>
								<Typography className="h3 sm:h2 font-medium">{`${name || 'Új elem'} beállításai`}</Typography>
							</div>

							<Typography className="w-full mx-8 text-16 mb-16" />

							<Card className="w-full rounded-10 shadow p-24 mb-16">
								{props.fieldConfig.fields.map(fieldRow =>
									_.isUndefined(fieldRow.visibleIf) || fieldRow.visibleIf(configWatchers, methods) ? (
										_.isUndefined(fieldRow.fields) && fieldRow.isControlledInput !== false ? (
											<Controller
												key={`${props.fieldPrefix}${fieldRow.key}`}
												name={`${props.fieldPrefix}${fieldRow.key}`}
												control={control}
												defaultValue={props.field[fieldRow.key]}
												render={({ field, fieldState }) => <fieldRow.component field={field} fieldState={fieldState} config={props.config} fieldConfig={fieldRow} fieldPrefix={props.fieldPrefix} />}
											/>
										) : (
											<fieldRow.component key={`${props.fieldPrefix}${fieldRow.key}`} name={`${props.fieldPrefix}${fieldRow.key}`} config={props.config} fieldConfig={fieldRow} fieldPrefix={props.fieldPrefix} />
										)
									) : null
								)}
							</Card>
						</div>
					</Card>
				</AccordionDetails>
			</Accordion>
			<ConfirmDialog show={showDeleteConfirm} title="Törlés" description="Biztosan törli a kiválasztott tételt?" onYes={() => props.onDelete()} onClose={() => setShowDeleteConfirm(false)} />
		</motion.div>
	);
}

export default EditFormSubItem;
